import React from 'react';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  allowEmpty?: boolean;
  emptyOptionLabel?: string;
  controllerSize?: 'sm' | 'md';
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, options, placeholder, allowEmpty = false, emptyOptionLabel, controllerSize = 'md', className = '', ...props }, ref) => {
    const sizeClass = controllerSize === 'sm' ? 'px-3 py-1.5 text-base' : 'px-3 py-2';

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm text-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={`
              w-full border border-gray-300 rounded-md shadow-sm
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              appearance-none bg-white pr-10
              ${sizeClass}
              ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
              ${className}
            `}
            {...props}
          >
            {!allowEmpty && placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {allowEmpty && (
              <option value="">
                {emptyOptionLabel ?? placeholder ?? ''}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>
        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
